import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Button,
	Group,
	TextInput,
	Table,
	Paper,
	Title,
	Text,
	Box,
	Modal,
	MediaQuery,
	FileInput,
	Progress,
	Pagination,
	Loader,
	Select,
	Stack,
	Menu,
	ActionIcon,
	Alert,
	Badge,
	Divider,
	ScrollArea,
	SimpleGrid,
	SegmentedControl,
	Tooltip,
} from '@mantine/core'
import {
	getExperienceCheckIns,
	uploadExperienceCheckIns,
	deleteCheckIn,
} from '../services/actions/checkInActions'
import {
	getStationOptions,
	getSegmentOptions,
	getItemOptions,
} from '../services/actions/experienceActions'
import { toast } from '../services/utils/toast'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import {
	AlertTriangle,
	Check,
	Eye,
	EyeOff,
	Upload,
	X,
} from 'tabler-icons-react'
import { parseUploadWarnings } from './warningUtils'
import { buildBookingPageUrl } from '../services/utils/bookingNumberLink'
import { normalizeCheckInUploadPhone } from '../services/utils/checkInUploadPhone'
import CheckInFilterBlock, {
	getCheckInCustomFields,
	matchesAdditionalCheckInFilters,
} from '../components/CheckInFilterBlock'

const CHECKIN_CSV_FIELDS = [
	'id',
	'groupId',
	'travelerName',
	'travelerEmail',
	'travelerPhone',
	'accountName',
	'scheduledCheckInDate',
	'checkOutDate',
	'stationName',
	'segmentName',
	'bookingNumber',
	'purchasedItems',
	'warnings',
	'checkInStatus',
]

const PAID_PAYMENT_STATUSES = new Set([
	'paid',
	'succeeded',
	'captured',
	'complete',
	'completed',
	'partially_refunded',
	'refunded',
])
const UNPAID_PAYMENT_METHODS = new Set(['', 'none', 'free', 'not recorded'])
const IGNORE_UPLOAD_COLUMN = '__livehq_ignore__'
const CUSTOM_UPLOAD_COLUMN = '__livehq_custom__'
const UPLOAD_ISSUE_PREVIEW_LIMIT = 10
const STAGED_ROWS_PAGE_SIZE = 25
const STAGED_ROWS_VIEW_UPLOAD = 'willUpload'
const STAGED_ROWS_VIEW_SKIP = 'willSkip'
const STAGED_ROWS_VIEW_MISSING = 'missingReferences'
const IMPORT_FIELD_DEFINITIONS = [
	{
		key: 'id',
		label: 'Check-in ID',
		aliases: ['checkInId', 'check in id', 'record id'],
	},
	{
		key: 'groupId',
		label: 'Group ID',
		aliases: ['group', 'party id', 'group booking'],
	},
	{
		key: 'travelerName',
		label: 'Traveler name',
		required: true,
		aliases: [
			'name',
			'full name',
			'guest name',
			'attendee name',
			'participant name',
			'customer name',
		],
	},
	{
		key: 'travelerEmail',
		label: 'Traveler email',
		aliases: ['email', 'email address', 'guest email', 'customer email'],
	},
	{
		key: 'travelerPhone',
		label: 'Traveler phone',
		aliases: ['phone', 'phone number', 'mobile', 'cell phone'],
	},
	{
		key: 'accountName',
		label: 'Account name',
		aliases: ['account', 'company', 'organization', 'school', 'team'],
	},
	{
		key: 'scheduledCheckInDate',
		label: 'Scheduled check-in date',
		aliases: [
			'scheduled date',
			'check-in date',
			'check in date',
			'arrival date',
			'date',
		],
	},
	{
		key: 'checkOutDate',
		label: 'Check-out date',
		aliases: ['check-out date', 'check out date', 'departure date'],
	},
	{
		key: 'stationName',
		label: 'Station name',
		aliases: ['station', 'location', 'check-in station', 'check in station'],
	},
	{
		key: 'segmentName',
		label: 'Segment name',
		aliases: ['segment', 'group segment', 'traveler segment'],
	},
	{
		key: 'bookingNumber',
		label: 'Booking number',
		aliases: [
			'booking',
			'reservation number',
			'order number',
			'confirmation number',
		],
	},
	{
		key: 'purchasedItems',
		label: 'Purchased items',
		aliases: ['items', 'products', 'passes', 'tickets'],
	},
	{
		key: 'warnings',
		label: 'Warnings',
		aliases: ['warning', 'alerts', 'alert'],
	},
	{
		key: 'checkInStatus',
		label: 'Check-in status (ignored)',
		referenceOnly: true,
		aliases: ['checked in', 'checked in?', 'status'],
	},
]

const IMPORT_FIELD_KEYS = new Set(
	IMPORT_FIELD_DEFINITIONS.map((field) => field.key),
)
const CORE_CSV_FIELD_SET = new Set(CHECKIN_CSV_FIELDS)
const IMPORT_MAPPING_OPTIONS = [
	{ value: IGNORE_UPLOAD_COLUMN, label: 'Ignore column' },
	...IMPORT_FIELD_DEFINITIONS.map((field) => ({
		value: field.key,
		label: field.required ? `${field.label} (required)` : field.label,
	})),
	{ value: CUSTOM_UPLOAD_COLUMN, label: 'Custom field' },
]

const normalizeHeaderKey = (value) =>
	String(value || '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '')

const IMPORT_FIELD_ALIAS_LOOKUP = IMPORT_FIELD_DEFINITIONS.reduce(
	(lookup, field) => {
		const aliases = [field.key, field.label, ...(field.aliases || [])]
		aliases.forEach((alias) => {
			const normalized = normalizeHeaderKey(alias)
			if (normalized && !lookup.has(normalized)) {
				lookup.set(normalized, field.key)
			}
		})
		return lookup
	},
	new Map(),
)

const hasPositivePaymentAmount = (item) => {
	const amountCents = Number(item?.paymentAmountCents)
	if (Number.isFinite(amountCents) && amountCents > 0) return true

	const rawAmount = String(item?.paymentAmount ?? '')
		.trim()
		.replace(/^\$/, '')
		.replace(/,/g, '')
	if (!rawAmount) return false

	const amount = Number(rawAmount)
	return Number.isFinite(amount) && amount > 0
}

const purchasedItemHasActualPayment = (item) => {
	if (!item || typeof item !== 'object' || Array.isArray(item)) return false

	const paymentStatus = String(item.paymentStatus || '')
		.trim()
		.toLowerCase()
	if (PAID_PAYMENT_STATUSES.has(paymentStatus)) return true

	const paymentMethod = String(item.paymentMethod || '')
		.trim()
		.toLowerCase()
	if (!UNPAID_PAYMENT_METHODS.has(paymentMethod)) return true

	return hasPositivePaymentAmount(item)
}

const checkInHasActualPaidPurchase = (checkIn) =>
	Array.isArray(checkIn?.purchasedItems) &&
	checkIn.purchasedItems.some(purchasedItemHasActualPayment)

const getCheckInDeleteBlockReason = (checkIn) => {
	if (!checkIn) return ''
	if (checkIn.checkInStatus) return 'Checked-in check-ins cannot be deleted.'
	if (checkInHasActualPaidPurchase(checkIn)) {
		return 'Check-ins with paid purchases cannot be deleted.'
	}
	return ''
}

const detectDevice = () => (window.innerWidth < 768 ? 'mobile' : 'desktop')

const normalizeCellValue = (value) => {
	if (value === null || value === undefined) return ''
	return String(value).trim()
}

const normalizeGroupIdValue = (value) => {
	const normalized = normalizeCellValue(value)
	return normalized.length > 0 ? normalized : null
}

const humanizeHeader = (value) => {
	const normalized = normalizeCellValue(value)
	if (!normalized) return 'Custom field'
	return normalized
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (char) => char.toUpperCase())
}

const getInitialUploadMappings = (headers) => {
	const usedFields = new Set()

	return headers.reduce((acc, header) => {
		const fieldKey = IMPORT_FIELD_ALIAS_LOOKUP.get(normalizeHeaderKey(header))
		if (fieldKey && !usedFields.has(fieldKey)) {
			acc[header] = fieldKey
			usedFields.add(fieldKey)
			return acc
		}

		acc[header] = CUSTOM_UPLOAD_COLUMN
		return acc
	}, {})
}

const getInitialCustomFieldNames = (headers, mappings) =>
	headers.reduce((acc, header) => {
		if (mappings[header] === CUSTOM_UPLOAD_COLUMN) {
			acc[header] = humanizeHeader(header)
		}
		return acc
	}, {})

const isBlankUploadRow = (row, headers) =>
	headers.every((header) => normalizeCellValue(row?.[header]).length === 0)

const getSampleCellValue = (rows, header) => {
	const sample = rows.find((row) => normalizeCellValue(row?.[header]))
	return normalizeCellValue(sample?.[header])
}

const normalizeCustomFieldName = (value) =>
	humanizeHeader(value).slice(0, 120).trim()

const parseUploadDate = (value) => {
	const raw = normalizeCellValue(value)
	if (!raw) return { date: null, error: '' }

	const excelSerial = Number(raw)
	if (/^\d+(\.\d+)?$/.test(raw) && excelSerial > 20000 && excelSerial < 80000) {
		const excelEpoch = Date.UTC(1899, 11, 30)
		const date = new Date(excelEpoch + excelSerial * 24 * 60 * 60 * 1000)
		return { date: date.toISOString(), error: '' }
	}

	const date = new Date(raw)
	if (Number.isNaN(date.getTime())) {
		return { date: null, error: 'Scheduled check-in date is not valid.' }
	}

	return { date: date.toISOString(), error: '' }
}

const parsePurchasedItemsForImport = (cell, itemLookups) => {
	const raw = normalizeCellValue(cell)
	if (!raw) return { items: [], missing: [] }

	const missing = []
	const resolveItem = (token) => {
		const normalized = normalizeCellValue(token)
		if (!normalized) return null

		const byId = itemLookups.byId.get(normalized)
		if (byId) return byId

		const byName = itemLookups.byName.get(normalized.toLowerCase())
		if (byName) return byName

		missing.push(normalized)
		return null
	}

	try {
		const parsed = JSON.parse(raw)
		if (Array.isArray(parsed)) {
			const items = parsed
				.map((item) => {
					if (typeof item === 'string') {
						const option = resolveItem(item)
						return option ? { id: option.id, issued: false } : null
					}

					if (!item || typeof item !== 'object') return null
					const option = resolveItem(item.id || item.name)
					return option ? { id: option.id, issued: item.issued ?? false } : null
				})
				.filter(Boolean)

			return { items, missing }
		}
	} catch {}

	const items = raw
		.split(',')
		.map((name) => resolveItem(name))
		.filter(Boolean)
		.map((option) => ({ id: option.id, issued: false }))

	return { items, missing }
}

const getCustomFieldExportKey = (key) =>
	CORE_CSV_FIELD_SET.has(key) ? `customFields.${key}` : key

const getCustomFieldExportColumns = (rows) =>
	Array.from(
		new Set(
			rows.flatMap((checkIn) => Object.keys(getCheckInCustomFields(checkIn))),
		),
	).sort((a, b) => a.localeCompare(b))

const buildCheckInCsvRows = (rows) => {
	const customFieldColumns = getCustomFieldExportColumns(rows)

	return rows.map((c) => {
		const customFields = getCheckInCustomFields(c)
		const exportedCustomFields = customFieldColumns.reduce((acc, key) => {
			acc[getCustomFieldExportKey(key)] = customFields[key] ?? ''
			return acc
		}, {})

		return {
			id: c.id,
			groupId: c.groupId || '',
			travelerName: c.travelerName,
			travelerEmail: c.travelerEmail,
			travelerPhone: c.travelerPhone,
			accountName: c.accountName,
			scheduledCheckInDate: c.scheduledCheckInDate,
			checkOutDate: c.checkOutDate,
			stationName: c.stationName || '',
			segmentName: c.segmentName || '',
			bookingNumber: c.bookingNumber,
			purchasedItems: JSON.stringify(c.purchasedItems || []),
			warnings: JSON.stringify(c.warnings || []),
			checkInStatus: c.checkInStatus ?? false,
			...exportedCustomFields,
		}
	})
}

const buildUploadPreview = ({
	headers,
	rows,
	columnMappings,
	customFieldNames,
	parseErrors,
	stationOptions,
	segmentOptions,
	itemOptions,
}) => {
	const mappingErrors = []
	const fieldUsage = new Map()
	const referenceOnlyFields = []

	headers.forEach((header) => {
		const mapping = columnMappings[header]
		if (!IMPORT_FIELD_KEYS.has(mapping)) return

		const field = IMPORT_FIELD_DEFINITIONS.find((item) => item.key === mapping)
		if (field?.referenceOnly) {
			referenceOnlyFields.push(field.label)
		}

		if (!fieldUsage.has(mapping)) {
			fieldUsage.set(mapping, [])
		}
		fieldUsage.get(mapping).push(header)
	})

	fieldUsage.forEach((mappedHeaders, fieldKey) => {
		if (mappedHeaders.length <= 1) return
		const field = IMPORT_FIELD_DEFINITIONS.find((item) => item.key === fieldKey)
		mappingErrors.push(
			`${field?.label || fieldKey} is assigned to multiple columns: ${mappedHeaders.join(', ')}.`,
		)
	})

	if (!fieldUsage.has('travelerName')) {
		mappingErrors.push('Assign one column to Traveler name before uploading.')
	}

	const stationLookups = {
		byId: new Map(stationOptions.map((station) => [station.id, station])),
		byName: new Map(
			stationOptions.map((station) => [
				normalizeCellValue(station.name).toLowerCase(),
				station,
			]),
		),
	}
	const segmentLookups = {
		byId: new Map(segmentOptions.map((segment) => [segment.id, segment])),
		byName: new Map(
			segmentOptions.map((segment) => [
				normalizeCellValue(segment.name).toLowerCase(),
				segment,
			]),
		),
	}
	const itemLookups = {
		byId: new Map(itemOptions.map((item) => [item.id, item])),
		byName: new Map(
			itemOptions.map((item) => [
				normalizeCellValue(item.name).toLowerCase(),
				item,
			]),
		),
	}

	const importedHeaders = headers.filter(
		(header) => columnMappings[header] !== IGNORE_UPLOAD_COLUMN,
	)
	const stagedRows = rows
		.filter((row) => !isBlankUploadRow(row, importedHeaders))
		.map((row, index) => {
			const fieldValues = {}
			const customFields = {}
			const rowNumber = row.__livehqRowNumber || index + 2

			headers.forEach((header) => {
				const mapping = columnMappings[header]
				const value = normalizeCellValue(row[header])

				if (mapping === IGNORE_UPLOAD_COLUMN) return

				if (mapping === CUSTOM_UPLOAD_COLUMN) {
					if (!value) return
					const customFieldName = normalizeCustomFieldName(
						customFieldNames[header] || header,
					)
					if (customFieldName) {
						customFields[customFieldName] = value
					}
					return
				}

				if (IMPORT_FIELD_KEYS.has(mapping)) {
					fieldValues[mapping] = value
				}
			})

			const errors = []
			const warnings = []
			const missingReferences = []
			const travelerName = normalizeCellValue(fieldValues.travelerName)

			if (!travelerName) {
				errors.push('Traveler name is required.')
			}

			const parsedDate = parseUploadDate(fieldValues.scheduledCheckInDate)
			if (parsedDate.error) {
				errors.push(parsedDate.error)
			}
			const parsedCheckOutDate = parseUploadDate(fieldValues.checkOutDate)
			if (parsedCheckOutDate.error) {
				errors.push(parsedCheckOutDate.error)
			}

			const stationToken = normalizeCellValue(fieldValues.stationName)
			let stationId = null
			if (stationToken) {
				const station =
					stationLookups.byId.get(stationToken) ||
					stationLookups.byName.get(stationToken.toLowerCase())
				if (station) {
					stationId = station.id
				} else {
					const message = `Station "${stationToken}" was not found.`
					warnings.push(message)
					missingReferences.push({
						type: 'Station',
						value: stationToken,
						rowNumber,
					})
				}
			}

			const segmentToken = normalizeCellValue(fieldValues.segmentName)
			let segmentId = null
			if (segmentToken) {
				const segment =
					segmentLookups.byId.get(segmentToken) ||
					segmentLookups.byName.get(segmentToken.toLowerCase())
				if (segment) {
					segmentId = segment.id
				} else {
					const message = `Segment "${segmentToken}" was not found.`
					warnings.push(message)
					missingReferences.push({
						type: 'Segment',
						value: segmentToken,
						rowNumber,
					})
				}
			}

			const purchasedItems = parsePurchasedItemsForImport(
				fieldValues.purchasedItems,
				itemLookups,
			)
			purchasedItems.missing.forEach((itemName) => {
				const message = `Purchased item "${itemName}" was not found.`
				warnings.push(message)
				missingReferences.push({
					type: 'Purchased item',
					value: itemName,
					rowNumber,
				})
			})

			const payload = {
				id: normalizeCellValue(fieldValues.id) || undefined,
				groupId: normalizeGroupIdValue(fieldValues.groupId),
				travelerName,
				travelerEmail: normalizeCellValue(fieldValues.travelerEmail) || null,
				travelerPhone: normalizeCheckInUploadPhone(fieldValues.travelerPhone),
				accountName: normalizeCellValue(fieldValues.accountName) || null,
				stationId,
				stationName: stationToken || undefined,
				segmentId,
				segmentName: segmentToken || undefined,
				bookingNumber: normalizeCellValue(fieldValues.bookingNumber) || null,
				scheduledCheckInDate: parsedDate.date,
				checkOutDate: parsedCheckOutDate.date,
				purchasedItems: purchasedItems.items,
				warnings: parseUploadWarnings(fieldValues.warnings),
				customFields,
			}

			return {
				rowNumber,
				payload,
				errors,
				warnings,
				missingReferences,
			}
		})

	const rowsWithErrors = stagedRows.filter((row) => row.errors.length > 0)
	const validRows = stagedRows.filter((row) => row.errors.length === 0)
	const missingReferences = stagedRows.flatMap((row) => row.missingReferences)
	const parserIssues = (parseErrors || []).map((error) => ({
		message: error.message || 'CSV parse error',
		row: Number.isInteger(error.row) ? error.row + 2 : null,
	}))
	const issueCount =
		mappingErrors.length + parserIssues.length + rowsWithErrors.length
	const canUpload = mappingErrors.length === 0 && validRows.length > 0

	return {
		mappingErrors,
		referenceOnlyFields,
		parserIssues,
		stagedRows,
		validRows,
		rowsWithErrors,
		missingReferences,
		canUpload,
		successCount: canUpload ? validRows.length : 0,
		errorCount: issueCount,
		skippedRowCount:
			mappingErrors.length > 0 ? stagedRows.length : rowsWithErrors.length,
	}
}

const summarizeMissingReferences = (missingReferences) => {
	const byKey = new Map()

	missingReferences.forEach((reference) => {
		const key = `${reference.type}:${reference.value}`
		const existing = byKey.get(key) || {
			...reference,
			count: 0,
			rows: [],
		}
		existing.count += 1
		existing.rows.push(reference.rowNumber)
		byKey.set(key, existing)
	})

	return Array.from(byKey.values()).sort((a, b) =>
		a.type === b.type
			? a.value.localeCompare(b.value)
			: a.type.localeCompare(b.type),
	)
}

const ManageCheckInsPage = () => {
	const { experienceSlug, orgSlug } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const experience = useSelector((state) => state.experience?.experienceDetails)

	const [checkIns, setCheckIns] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [showUploadModal, setShowUploadModal] = useState(false)
	const [uploadFile, setUploadFile] = useState(null)
	const [uploadParseState, setUploadParseState] = useState({
		status: 'idle',
		fileName: '',
		headers: [],
		rows: [],
		parseErrors: [],
	})
	const [columnMappings, setColumnMappings] = useState({})
	const [lastIncludedColumnMappings, setLastIncludedColumnMappings] = useState(
		{},
	)
	const [customFieldNames, setCustomFieldNames] = useState({})
	const [uploadResult, setUploadResult] = useState(null)
	const [stagedRowsView, setStagedRowsView] = useState(STAGED_ROWS_VIEW_UPLOAD)
	const [stagedRowsPage, setStagedRowsPage] = useState(1)
	const [searchTerm, setSearchTerm] = useState('')
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [checkInToDelete, setCheckInToDelete] = useState(null)
	const [uploadProgress, setUploadProgress] = useState(null)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(50)
	const [stationOptions, setStationOptions] = useState([])
	const [segmentOptions, setSegmentOptions] = useState([])
	const [itemOptions, setItemOptions] = useState([])
	const [selectedStations, setSelectedStations] = useState([])
	const [itemFilter, setItemFilter] = useState([])
	const [checkInFilter, setCheckInFilter] = useState('all')
	const [customFieldFilters, setCustomFieldFilters] = useState([])

	const fetchStations = useCallback(async () => {
		try {
			const stations = await getStationOptions(experienceSlug, orgSlug)
			setStationOptions(stations)
		} catch (err) {
			console.error('Failed to fetch stations:', err)
		}
	}, [experienceSlug, orgSlug])

	const fetchSegments = useCallback(async () => {
		try {
			const segments = await getSegmentOptions(experienceSlug, orgSlug)
			setSegmentOptions(segments)
		} catch (err) {
			console.error('Failed to fetch segments:', err)
		}
	}, [experienceSlug, orgSlug])

	const fetchItems = useCallback(async () => {
		try {
			const items = await getItemOptions(experienceSlug, orgSlug)
			setItemOptions(items)
		} catch (err) {
			console.error('Failed to fetch items:', err)
		}
	}, [experienceSlug, orgSlug])

	const fetchCheckIns = useCallback(async () => {
		setLoading(true)
		try {
			const results = await getExperienceCheckIns(experienceSlug, orgSlug)
			setCheckIns(results)
		} catch (err) {
			console.error('Failed to fetch check-ins:', err)
			setError('Failed to load check-ins')
		} finally {
			setLoading(false)
		}
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		const size = detectDevice()
		setPageSize(size === 'mobile' ? 20 : 50)
		fetchCheckIns()
		fetchStations()
		fetchSegments()
		fetchItems()
	}, [fetchCheckIns, fetchItems, fetchSegments, fetchStations])

	const handleDownloadCheckIns = () => {
		const sample =
			checkIns.length > 0
				? checkIns
				: [
						{
							id: '',
							groupId: 'GROUP-1001',
							travelerName: 'John Doe',
							travelerEmail: 'john@example.com',
							travelerPhone: '555-555-5555',
							accountName: 'Ohio University',
							scheduledCheckInDate: new Date('2025-02-08'),
							checkOutDate: new Date('2025-02-12'),
							stationName: 'Front Desk',
							segmentName: 'VIP',
							bookingNumber: 'BOOK-12345',
							purchasedItems: [{ name: 'Day Pass', issued: false }],
							warnings: [
								{
									title: 'Needs to pay',
									level: 'Warning',
									resolutionLink: 'https://www.example.com',
								},
							],
							checkInStatus: false,
						},
					]

		const formatted = buildCheckInCsvRows(sample)

		const csv =
			formatted.length > 0
				? Papa.unparse(formatted)
				: Papa.unparse({ fields: CHECKIN_CSV_FIELDS, data: [] })
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, `${experienceSlug}_checkins.csv`)
	}

	const resetUploadState = () => {
		setUploadFile(null)
		setUploadParseState({
			status: 'idle',
			fileName: '',
			headers: [],
			rows: [],
			parseErrors: [],
		})
		setColumnMappings({})
		setLastIncludedColumnMappings({})
		setCustomFieldNames({})
		setUploadResult(null)
		setUploadProgress(null)
		setStagedRowsView(STAGED_ROWS_VIEW_UPLOAD)
		setStagedRowsPage(1)
	}

	const closeUploadModal = () => {
		if (uploadProgress !== null) return
		setShowUploadModal(false)
		resetUploadState()
	}

	const handleFileUpload = (file) => {
		setUploadFile(file)
		setUploadResult(null)

		if (!file) {
			resetUploadState()
			return
		}

		setUploadParseState({
			status: 'parsing',
			fileName: file.name,
			headers: [],
			rows: [],
			parseErrors: [],
		})

		Papa.parse(file, {
			header: true,
			skipEmptyLines: 'greedy',
			transformHeader: (header) => normalizeCellValue(header),
			complete: (results) => {
				const headers = (results.meta?.fields || []).filter(Boolean)
				const mappings = getInitialUploadMappings(headers)
				const rows = (results.data || []).map((row, index) => ({
					...row,
					__livehqRowNumber: index + 2,
				}))

				setColumnMappings(mappings)
				setLastIncludedColumnMappings(mappings)
				setCustomFieldNames(getInitialCustomFieldNames(headers, mappings))
				setUploadParseState({
					status: headers.length > 0 ? 'ready' : 'error',
					fileName: file.name,
					headers,
					rows,
					parseErrors:
						headers.length > 0
							? results.errors || []
							: [{ message: 'No header row was found in this CSV.' }],
				})
			},
			error: (err) => {
				setUploadParseState({
					status: 'error',
					fileName: file.name,
					headers: [],
					rows: [],
					parseErrors: [{ message: err.message || 'Failed to parse CSV.' }],
				})
			},
		})
	}

	const handleColumnMappingChange = (header, value) => {
		if (value !== IGNORE_UPLOAD_COLUMN) {
			setLastIncludedColumnMappings((prev) => ({
				...prev,
				[header]: value,
			}))
		}
		setColumnMappings((prev) => ({ ...prev, [header]: value }))
		setCustomFieldNames((prev) => {
			if (value !== CUSTOM_UPLOAD_COLUMN || prev[header]) return prev
			return { ...prev, [header]: humanizeHeader(header) }
		})
		setUploadResult(null)
	}

	const handleColumnVisibilityToggle = (header) => {
		const isIgnored = columnMappings[header] === IGNORE_UPLOAD_COLUMN
		handleColumnMappingChange(
			header,
			isIgnored
				? lastIncludedColumnMappings[header] || CUSTOM_UPLOAD_COLUMN
				: IGNORE_UPLOAD_COLUMN,
		)
	}

	const handleCustomFieldNameChange = (header, value) => {
		setCustomFieldNames((prev) => ({ ...prev, [header]: value }))
		setUploadResult(null)
	}

	const uploadPreview = useMemo(() => {
		if (!uploadParseState.headers.length) return null

		return buildUploadPreview({
			headers: uploadParseState.headers,
			rows: uploadParseState.rows,
			columnMappings,
			customFieldNames,
			parseErrors: uploadParseState.parseErrors,
			stationOptions,
			segmentOptions,
			itemOptions,
		})
	}, [
		columnMappings,
		customFieldNames,
		itemOptions,
		segmentOptions,
		stationOptions,
		uploadParseState.headers,
		uploadParseState.parseErrors,
		uploadParseState.rows,
	])

	const missingReferenceSummary = useMemo(
		() => summarizeMissingReferences(uploadPreview?.missingReferences || []),
		[uploadPreview],
	)

	const stagedRowsForView = useMemo(() => {
		if (!uploadPreview) return []

		if (stagedRowsView === STAGED_ROWS_VIEW_SKIP) {
			return uploadPreview.mappingErrors.length > 0
				? uploadPreview.stagedRows
				: uploadPreview.rowsWithErrors
		}

		if (stagedRowsView === STAGED_ROWS_VIEW_MISSING) {
			return uploadPreview.stagedRows.filter(
				(row) => row.missingReferences.length > 0,
			)
		}

		return uploadPreview.validRows
	}, [stagedRowsView, uploadPreview])

	const stagedRowsTotalPages = Math.max(
		1,
		Math.ceil(stagedRowsForView.length / STAGED_ROWS_PAGE_SIZE),
	)
	const paginatedStagedRows = stagedRowsForView.slice(
		(stagedRowsPage - 1) * STAGED_ROWS_PAGE_SIZE,
		stagedRowsPage * STAGED_ROWS_PAGE_SIZE,
	)
	const stagedRowsStart =
		stagedRowsForView.length > 0
			? (stagedRowsPage - 1) * STAGED_ROWS_PAGE_SIZE + 1
			: 0
	const stagedRowsEnd =
		stagedRowsForView.length > 0
			? Math.min(
					stagedRowsPage * STAGED_ROWS_PAGE_SIZE,
					stagedRowsForView.length,
				)
			: 0

	useEffect(() => {
		setStagedRowsPage(1)
	}, [
		stagedRowsView,
		uploadPreview?.stagedRows.length,
		uploadPreview?.successCount,
		uploadPreview?.skippedRowCount,
		uploadPreview?.missingReferences.length,
	])

	useEffect(() => {
		if (stagedRowsPage > stagedRowsTotalPages) {
			setStagedRowsPage(stagedRowsTotalPages)
		}
	}, [stagedRowsPage, stagedRowsTotalPages])

	const handleConfirmUpload = async () => {
		if (!uploadPreview?.canUpload || uploadProgress !== null) return

		const payload = uploadPreview.validRows.map((row) => row.payload)
		setUploadProgress(15)
		setUploadResult(null)

		try {
			const result = await uploadExperienceCheckIns(
				experienceSlug,
				orgSlug,
				payload,
			)
			setUploadProgress(100)
			setUploadResult({
				type: 'success',
				requested: payload.length,
				skipped: uploadPreview.skippedRowCount,
				missingReferenceCount: uploadPreview.missingReferences.length,
				summary: result?.importSummary,
				stationWarnings: result?.stationWarnings || [],
				segmentWarnings: result?.segmentWarnings || [],
			})
			toast.success({
				title: 'Check-ins uploaded',
				message: `${payload.length} valid row${payload.length === 1 ? '' : 's'} processed.`,
			})
			await fetchCheckIns()
		} catch (err) {
			console.error('Failed to upload check-ins:', err)
			setUploadResult({
				type: 'error',
				message: err.message || 'Failed to upload check-ins.',
			})
			toast.error({
				title: 'Upload failed',
				message: err.message || 'Failed to upload check-ins.',
			})
		} finally {
			setUploadProgress(null)
		}
	}

	const handleDeleteConfirmed = async () => {
		const blockReason = getCheckInDeleteBlockReason(checkInToDelete)
		if (blockReason) {
			toast.error({
				title: 'Check-in cannot be deleted',
				message: blockReason,
			})
			setDeleteModalOpen(false)
			setCheckInToDelete(null)
			return
		}

		try {
			const travelerName = checkInToDelete?.travelerName || 'Traveler'
			await deleteCheckIn(checkInToDelete.id, orgSlug)
			toast.success({
				title: 'Check-in deleted',
				message: `${travelerName}'s check-in was deleted successfully.`,
			})
			setDeleteModalOpen(false)
			setCheckInToDelete(null)
			await fetchCheckIns()
		} catch (err) {
			console.error('Failed to delete check-in:', err)
			setError(err.message || 'Failed to delete check-in')
		}
	}

	const renderDeleteButton = (checkIn) => {
		const blockReason = getCheckInDeleteBlockReason(checkIn)

		return (
			<Button
				size='xs'
				color='red'
				disabled={Boolean(blockReason)}
				title={blockReason || 'Delete check-in'}
				onClick={() => {
					if (blockReason) return
					setCheckInToDelete(checkIn)
					setDeleteModalOpen(true)
				}}
			>
				Delete
			</Button>
		)
	}

	const handleViewMessageHistory = (checkInId) => {
		if (!checkInId) return
		navigate(
			`/${orgSlug}/experiences/${experienceSlug}/checkIn/${checkInId}/messages`,
			{
				state: {
					returnTo: `${location.pathname}${location.search}${location.hash}`,
				},
			},
		)
	}

	const renderCheckInActionsMenu = (checkIn) => {
		const bookingPageUrl = buildBookingPageUrl(
			experience?.bookingNumberLink,
			checkIn.bookingNumber,
		)

		return (
			<Menu position='bottom-end' withinPortal>
				<Menu.Target>
					<ActionIcon
						variant='light'
						size='lg'
						aria-label={`More actions for ${checkIn.travelerName || 'check-in'}`}
					>
						⋮
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown>
					{bookingPageUrl && (
						<Menu.Item
							component='a'
							href={bookingPageUrl}
							target='_blank'
							rel='noopener noreferrer'
						>
							Visit booking page
						</Menu.Item>
					)}
					<Menu.Item
						component='a'
						href={`/myexperience/${checkIn.id}`}
						target='_blank'
						rel='noopener noreferrer'
					>
						View experience page
					</Menu.Item>
					<Menu.Item onClick={() => handleViewMessageHistory(checkIn.id)}>
						View message history
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>
		)
	}

	const filteredCheckIns = checkIns.filter((c) => {
		const term = searchTerm.toLowerCase()
		const customFieldText = Object.values(getCheckInCustomFields(c)).join(' ')
		const matchesSearch =
			c.travelerName?.toLowerCase().includes(term) ||
			c.travelerEmail?.toLowerCase().includes(term) ||
			c.travelerPhone?.toLowerCase().includes(term) ||
			c.bookingNumber?.toLowerCase().includes(term) ||
			c.groupId?.toLowerCase().includes(term) ||
			customFieldText.toLowerCase().includes(term)

		const matchesStation =
			selectedStations.length === 0 ||
			selectedStations.some(
				(station) => c.stationName?.toLowerCase() === station.toLowerCase(),
			)

		const matchesCheckInStatus =
			checkInFilter === 'all' ||
			(checkInFilter === 'checkedIn' && c.checkInStatus) ||
			(checkInFilter === 'notCheckedIn' && !c.checkInStatus)

		const matchesPurchasedItems =
			itemFilter.length === 0 ||
			(Array.isArray(c.purchasedItems) &&
				c.purchasedItems.some((item) => itemFilter.includes(item?.id)))
		const matchesAdditionalFilters = matchesAdditionalCheckInFilters(
			c,
			customFieldFilters,
		)

		return (
			matchesSearch &&
			matchesStation &&
			matchesCheckInStatus &&
			matchesPurchasedItems &&
			matchesAdditionalFilters
		)
	})

	const handleDownloadFilteredResults = () => {
		const formatted = buildCheckInCsvRows(filteredCheckIns)

		const csv =
			formatted.length > 0
				? Papa.unparse(formatted)
				: Papa.unparse({ fields: CHECKIN_CSV_FIELDS, data: [] })
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, `${experienceSlug}_checkins_results.csv`)
	}

	const hasActiveFilters =
		searchTerm.trim().length > 0 ||
		selectedStations.length > 0 ||
		checkInFilter !== 'all' ||
		itemFilter.length > 0 ||
		customFieldFilters.some(
			(filter) => filter.field || filter.values.length > 0,
		)

	useEffect(() => {
		setPage(1)
	}, [
		searchTerm,
		selectedStations,
		checkInFilter,
		itemFilter,
		customFieldFilters,
	])

	const paginatedCheckIns = filteredCheckIns.slice(
		(page - 1) * pageSize,
		page * pageSize,
	)
	const totalPages = Math.ceil(filteredCheckIns.length / pageSize)
	const totalCheckInCount = checkIns.length
	const filteredCheckInCount = filteredCheckIns.length
	const currentStart = filteredCheckInCount > 0 ? (page - 1) * pageSize + 1 : 0
	const currentEnd =
		filteredCheckInCount > 0
			? Math.min(page * pageSize, filteredCheckInCount)
			: 0

	const checkInCountLabel = hasActiveFilters
		? filteredCheckInCount > 0
			? `Showing ${currentStart}-${currentEnd} of ${filteredCheckInCount} filtered check-ins (${totalCheckInCount} total)`
			: `Showing 0 filtered check-ins (${totalCheckInCount} total)`
		: totalCheckInCount > 0
			? `Showing ${currentStart}-${currentEnd} of ${totalCheckInCount} check-ins`
			: 'Showing 0 check-ins'
	const checkInToDeleteBlockReason =
		getCheckInDeleteBlockReason(checkInToDelete)

	return (
		<Box
			px='md'
			py='md'
			sx={(theme) => ({
				[theme.fn.smallerThan('sm')]: { paddingLeft: 0, paddingRight: 0 },
			})}
		>
			<Box
				mb='md'
				sx={(theme) => ({
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					gap: theme.spacing.md,
					[theme.fn.smallerThan('sm')]: {
						flexDirection: 'column',
						alignItems: 'flex-start',
					},
				})}
			>
				<Box>
					<Title order={2}>Manage Check-Ins</Title>
					{error && <Text color='red'>{error}</Text>}
				</Box>

				<Group>
					<Button
						onClick={() =>
							navigate(
								`/${orgSlug}/experiences/${experienceSlug}/manage/checkin/new`,
							)
						}
					>
						Add Check-In
					</Button>
					{hasActiveFilters ? (
						<Menu position='bottom-start' withinPortal>
							<Menu.Target>
								<Button rightSection='▾'>
									Download
								</Button>
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item onClick={handleDownloadCheckIns}>
									Download All
								</Menu.Item>
								<Menu.Item onClick={handleDownloadFilteredResults}>
									Download Results
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>
					) : (
						<Button onClick={handleDownloadCheckIns}>
							Download
						</Button>
					)}
					{experience.allowCheckInMassImport && (
						<Button
							leftIcon={<Upload size={16} />}
							onClick={() => setShowUploadModal(true)}
						>
							Upload
						</Button>
					)}
				</Group>
			</Box>

			<CheckInFilterBlock
				emphasizeAddFilter
				checkIns={checkIns}
				searchTerm={searchTerm}
				onSearchTermChange={setSearchTerm}
				checkInStatus={checkInFilter}
				onCheckInStatusChange={setCheckInFilter}
				stationFilter={selectedStations}
				onStationFilterChange={setSelectedStations}
				stationOptions={stationOptions.map((station) => ({
					value: station.name,
					label: station.name,
				}))}
				itemFilter={itemFilter}
				onItemFilterChange={setItemFilter}
				itemOptions={itemOptions.map((item) => ({
					value: item.id,
					label: item.name,
				}))}
				filters={customFieldFilters}
				onFiltersChange={setCustomFieldFilters}
			/>

			{loading ? (
				<Loader />
			) : paginatedCheckIns.length === 0 ? (
				<Text>No check-ins found</Text>
			) : (
				<>
					<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
						<Box>
							{paginatedCheckIns.map((c) => (
								<Paper key={c.id} withBorder radius='md' p='sm' mb='sm'>
									<Text fw={600}>{c.travelerName}</Text>
									<Text size='xs' c='dimmed'>
										{c.travelerEmail}
									</Text>
									<Text size='xs' c='dimmed'>
										{c.travelerPhone}
									</Text>
									<Text size='xs' c='dimmed'>
										{c.accountName}
									</Text>
									<Text size='xs'>
										Date: {c.scheduledCheckInDate?.slice(0, 10)}
									</Text>
									<Text size='xs'>Station: {c.stationName}</Text>
									<Text size='xs'>Booking: {c.bookingNumber}</Text>
									<Text size='xs'>
										Checked In?: {c.checkInStatus ? '✅ Yes' : '❌ No'}
									</Text>
									<Group mt='xs'>
										<Button
											size='xs'
											style={{ width: 85 }}
											onClick={() =>
												navigate(
													`/${orgSlug}/experiences/${experienceSlug}/checkIn/${c.id}`,
												)
											}
										>
											{c.checkInStatus ? 'View' : 'Check In'}
										</Button>
										<Button
											size='xs'
											onClick={() =>
												navigate(
													`/${orgSlug}/experiences/${experienceSlug}/manage/checkin/${c.id}`,
												)
											}
										>
											Edit
										</Button>
										{renderDeleteButton(c)}
										{renderCheckInActionsMenu(c)}
									</Group>
								</Paper>
							))}
						</Box>
					</MediaQuery>

					<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
						<Box>
							<Paper withBorder radius='md' p='sm'>
								<Table striped highlightOnHover>
									<thead>
										<tr>
											<th>Booking</th>
											<th>Name</th>
											<th>Email</th>
											<th>Checked In?</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{paginatedCheckIns.map((c) => (
											<tr key={c.id}>
												<td>{c.bookingNumber}</td>
												<td>{c.travelerName}</td>
												<td>
													{c.travelerEmail && c.travelerEmail.length > 25
														? `${c.travelerEmail.slice(0, 22)}...`
														: c.travelerEmail || ''}
												</td>
												<td>{c.checkInStatus ? '✅ Yes' : '❌ No'}</td>
												<td>
													<Group>
														<Button
															size='xs'
															style={{ width: 85 }}
															onClick={() =>
																navigate(
																	`/${orgSlug}/experiences/${experienceSlug}/checkIn/${c.id}`,
																)
															}
														>
															{c.checkInStatus ? 'View' : 'Check In'}
														</Button>
														<Button
															size='xs'
															onClick={() =>
																navigate(
																	`/${orgSlug}/experiences/${experienceSlug}/manage/checkin/${c.id}`,
																)
															}
														>
															Edit
														</Button>
														{renderDeleteButton(c)}
														{renderCheckInActionsMenu(c)}
													</Group>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</Paper>
						</Box>
					</MediaQuery>
					<Box mt='sm'>
						<Text size='sm' c='dimmed' mb={totalPages > 1 ? 'xs' : 0}>
							{checkInCountLabel}
						</Text>
						{totalPages > 1 && (
							<Pagination
								value={page}
								onChange={setPage}
								total={totalPages}
								size='sm'
							/>
						)}
					</Box>
				</>
			)}

			<Modal
				opened={showUploadModal}
				onClose={closeUploadModal}
				title='Upload Check-Ins'
				size='90%'
				withCloseButton={uploadProgress === null}
				closeOnClickOutside={uploadProgress === null}
				closeOnEscape={uploadProgress === null}
			>
				<Stack spacing='md'>
					<FileInput
						label='CSV file'
						accept='.csv,text/csv'
						value={uploadFile}
						onChange={handleFileUpload}
						placeholder='Select a .csv file'
						withAsterisk
						clearable
						disabled={uploadProgress !== null}
					/>

					{uploadParseState.status === 'idle' && (
						<Alert color='blue'>
							<Text size='sm'>
								Upload a CSV to stage it here first. LiveHQ will auto-match
								known headers, treat unknown columns as custom fields, and show
								rows that need attention before anything is saved.
							</Text>
						</Alert>
					)}

					{uploadParseState.status === 'parsing' && (
						<Box>
							<Text size='sm' mb='xs'>
								Reading {uploadParseState.fileName}...
							</Text>
							<Progress value={35} striped animated />
						</Box>
					)}

					{uploadParseState.status === 'error' && (
						<Alert
							icon={<X size={16} />}
							color='red'
							title='CSV could not be staged'
						>
							{uploadParseState.parseErrors.map((issue, index) => (
								<Text key={index} size='sm'>
									{issue.message}
								</Text>
							))}
						</Alert>
					)}

					{uploadPreview && (
						<>
							<SimpleGrid
								cols={4}
								spacing='sm'
								breakpoints={[
									{ maxWidth: 'md', cols: 2 },
									{ maxWidth: 'xs', cols: 1 },
								]}
							>
								<Paper withBorder p='sm'>
									<Text size='xs' color='dimmed'>
										Rows staged
									</Text>
									<Title order={4}>{uploadPreview.stagedRows.length}</Title>
								</Paper>
								<Paper withBorder p='sm'>
									<Text size='xs' color='dimmed'>
										Will upload
									</Text>
									<Title order={4} color='green'>
										{uploadPreview.successCount}
									</Title>
								</Paper>
								<Paper withBorder p='sm'>
									<Text size='xs' color='dimmed'>
										Will skip
									</Text>
									<Title
										order={4}
										color={uploadPreview.skippedRowCount ? 'red' : 'gray'}
									>
										{uploadPreview.skippedRowCount}
									</Title>
								</Paper>
								<Paper withBorder p='sm'>
									<Text size='xs' color='dimmed'>
										Missing references
									</Text>
									<Title
										order={4}
										color={
											uploadPreview.missingReferences.length ? 'orange' : 'gray'
										}
									>
										{uploadPreview.missingReferences.length}
									</Title>
								</Paper>
							</SimpleGrid>

							{uploadPreview.referenceOnlyFields.length > 0 && (
								<Alert color='blue' icon={<AlertTriangle size={16} />}>
									<Text size='sm'>
										Check-in status columns are shown for review only. Uploads
										do not mark travelers checked in or unchecked.
									</Text>
								</Alert>
							)}

							{uploadPreview.mappingErrors.length > 0 && (
								<Alert
									color='red'
									icon={<X size={16} />}
									title='Column mapping needs attention'
								>
									{uploadPreview.mappingErrors.map((issue, index) => (
										<Text key={index} size='sm'>
											{issue}
										</Text>
									))}
								</Alert>
							)}

							{uploadPreview.parserIssues.length > 0 && (
								<Alert
									color='orange'
									icon={<AlertTriangle size={16} />}
									title='CSV parser notices'
								>
									{uploadPreview.parserIssues
										.slice(0, UPLOAD_ISSUE_PREVIEW_LIMIT)
										.map((issue, index) => (
											<Text key={index} size='sm'>
												{issue.row ? `Row ${issue.row}: ` : ''}
												{issue.message}
											</Text>
										))}
								</Alert>
							)}

							<Box>
								<Group position='apart' mb='xs'>
									<Text fw={600}>Column mapping</Text>
									<Group spacing='xs'>
										<Badge variant='light'>
											{
												Object.values(columnMappings).filter(
													(value) => value === CUSTOM_UPLOAD_COLUMN,
												).length
											}{' '}
											custom
										</Badge>
										<Badge variant='light' color='gray'>
											{
												Object.values(columnMappings).filter(
													(value) => value === IGNORE_UPLOAD_COLUMN,
												).length
											}{' '}
											ignored
										</Badge>
									</Group>
								</Group>
								<ScrollArea type='auto'>
									<Table
										striped
										highlightOnHover
										fontSize='sm'
										style={{ minWidth: 1180, tableLayout: 'fixed' }}
									>
										<thead>
											<tr>
												<th style={{ width: 240 }}>CSV column</th>
												<th style={{ width: 320 }}>Assign to</th>
												<th style={{ width: 280 }}>Custom field name</th>
												<th>Sample</th>
											</tr>
										</thead>
										<tbody>
											{uploadParseState.headers.map((header) => {
												const isIgnored =
													columnMappings[header] === IGNORE_UPLOAD_COLUMN

												return (
													<tr
														key={header}
														style={{
															opacity: isIgnored ? 0.55 : 1,
															filter: isIgnored ? 'grayscale(1)' : 'none',
															transition: 'opacity 120ms ease',
														}}
													>
														<td>
															<Text size='sm' fw={600} lineClamp={2}>
																{header}
															</Text>
														</td>
														<td>
															<Group spacing='xs' noWrap>
																<Tooltip
																	label={isIgnored ? 'Show' : 'Ignore'}
																	withinPortal
																>
																	<ActionIcon
																		size='lg'
																		variant={isIgnored ? 'filled' : 'subtle'}
																		color='gray'
																		onClick={() =>
																			handleColumnVisibilityToggle(header)
																		}
																		aria-label={`${isIgnored ? 'Show' : 'Ignore'} ${header} column`}
																		aria-pressed={isIgnored}
																	>
																		{isIgnored ? (
																			<EyeOff size={18} />
																		) : (
																			<Eye size={18} />
																		)}
																	</ActionIcon>
																</Tooltip>
																<Select
																	style={{ flex: 1 }}
																	value={
																		columnMappings[header] ||
																		CUSTOM_UPLOAD_COLUMN
																	}
																	onChange={(value) =>
																		handleColumnMappingChange(
																			header,
																			value || IGNORE_UPLOAD_COLUMN,
																		)
																	}
																	data={IMPORT_MAPPING_OPTIONS}
																	withinPortal
																	searchable
																	clearable
																	disabled={isIgnored}
																/>
															</Group>
														</td>
														<td>
															<TextInput
																style={{ width: '100%' }}
																value={customFieldNames[header] || ''}
																onChange={(event) =>
																	handleCustomFieldNameChange(
																		header,
																		event.currentTarget.value,
																	)
																}
																disabled={
																	columnMappings[header] !==
																	CUSTOM_UPLOAD_COLUMN
																}
																placeholder='Field label'
															/>
														</td>
														<td>
															<Text size='sm' color='dimmed' lineClamp={2}>
																{getSampleCellValue(
																	uploadParseState.rows,
																	header,
																) || '-'}
															</Text>
														</td>
													</tr>
												)
											})}
										</tbody>
									</Table>
								</ScrollArea>
							</Box>

							{missingReferenceSummary.length > 0 && (
								<Alert
									color='orange'
									icon={<AlertTriangle size={16} />}
									title='Missing references'
								>
									<Text size='sm' mb='xs'>
										These rows can still upload, but unmatched stations and
										segments will stay blank and unmatched purchased items will
										not be attached.
									</Text>
									{missingReferenceSummary
										.slice(0, UPLOAD_ISSUE_PREVIEW_LIMIT)
										.map((reference) => (
											<Text
												key={`${reference.type}-${reference.value}`}
												size='sm'
											>
												<strong>{reference.type}:</strong> {reference.value} (
												{reference.count} row{reference.count === 1 ? '' : 's'},
												first rows {reference.rows.slice(0, 5).join(', ')})
											</Text>
										))}
								</Alert>
							)}

							{uploadPreview.rowsWithErrors.length > 0 && (
								<Alert
									color='red'
									icon={<X size={16} />}
									title='Rows that will be skipped'
								>
									{uploadPreview.rowsWithErrors
										.slice(0, UPLOAD_ISSUE_PREVIEW_LIMIT)
										.map((row) => (
											<Text key={row.rowNumber} size='sm'>
												Row {row.rowNumber}: {row.errors.join('; ')}
											</Text>
										))}
								</Alert>
							)}

							<Box>
								<Stack spacing='xs'>
									<Group position='apart' align='flex-end'>
										<Box>
											<Text fw={600}>Staged rows</Text>
											<Text size='sm' color='dimmed'>
												{stagedRowsForView.length > 0
													? `Showing ${stagedRowsStart}-${stagedRowsEnd} of ${stagedRowsForView.length}`
													: 'No rows in this view'}
											</Text>
										</Box>
										<SegmentedControl
											value={stagedRowsView}
											onChange={setStagedRowsView}
											data={[
												{
													value: STAGED_ROWS_VIEW_UPLOAD,
													label: `Will upload (${uploadPreview.successCount})`,
												},
												{
													value: STAGED_ROWS_VIEW_SKIP,
													label: `Will skip (${uploadPreview.skippedRowCount})`,
												},
												{
													value: STAGED_ROWS_VIEW_MISSING,
													label: `Missing references (${uploadPreview.missingReferences.length})`,
												},
											]}
										/>
									</Group>
									<ScrollArea style={{ height: 320 }} type='auto'>
										<Table
											striped
											highlightOnHover
											fontSize='sm'
											style={{ minWidth: 860 }}
										>
											<thead>
												<tr>
													<th>Status</th>
													<th>Row</th>
													<th>Name</th>
													<th>Email</th>
													<th>Station</th>
													<th>Segment</th>
													<th>Custom fields</th>
													<th>Notes</th>
												</tr>
											</thead>
											<tbody>
												{paginatedStagedRows.length === 0 ? (
													<tr>
														<td colSpan={8}>
															<Text
																size='sm'
																color='dimmed'
																align='center'
																py='xl'
															>
																No staged rows match this view.
															</Text>
														</td>
													</tr>
												) : (
													paginatedStagedRows.map((row) => {
														const statusColor = row.errors.length
															? 'red'
															: row.warnings.length
																? 'orange'
																: 'green'
														const statusLabel = row.errors.length
															? 'Error'
															: row.warnings.length
																? 'Warning'
																: 'Ready'
														const mappingIssueNote =
															stagedRowsView === STAGED_ROWS_VIEW_SKIP &&
															uploadPreview.mappingErrors.length > 0 &&
															row.errors.length === 0
																? 'Fix column mapping before upload.'
																: ''
														return (
															<tr key={row.rowNumber}>
																<td>
																	<Badge color={statusColor} variant='light'>
																		{statusLabel}
																	</Badge>
																</td>
																<td>{row.rowNumber}</td>
																<td>{row.payload.travelerName || '-'}</td>
																<td>{row.payload.travelerEmail || '-'}</td>
																<td>{row.payload.stationName || '-'}</td>
																<td>{row.payload.segmentName || '-'}</td>
																<td>
																	{
																		Object.keys(row.payload.customFields || {})
																			.length
																	}
																</td>
																<td>
																	<Text size='sm' lineClamp={2}>
																		{[
																			...row.errors,
																			...row.warnings,
																			mappingIssueNote,
																		]
																			.filter(Boolean)
																			.join('; ') || 'Ready to upload'}
																	</Text>
																</td>
															</tr>
														)
													})
												)}
											</tbody>
										</Table>
									</ScrollArea>
									<Group position='apart'>
										<Text size='xs' color='dimmed'>
											{STAGED_ROWS_PAGE_SIZE} rows per page
										</Text>
										{stagedRowsTotalPages > 1 && (
											<Pagination
												value={stagedRowsPage}
												onChange={setStagedRowsPage}
												total={stagedRowsTotalPages}
												size='sm'
											/>
										)}
									</Group>
								</Stack>
							</Box>

							{uploadProgress !== null && (
								<Box>
									<Text size='sm' mb='xs'>
										Uploading staged rows...
									</Text>
									<Progress value={uploadProgress} striped animated />
								</Box>
							)}

							{uploadResult?.type === 'success' && (
								<Alert
									color='green'
									icon={<Check size={16} />}
									title='Upload complete'
								>
									<Text size='sm'>
										{uploadResult.summary
											? `${uploadResult.summary.created} created, ${uploadResult.summary.updated} updated, ${uploadResult.summary.skipped} skipped by the server.`
											: `${uploadResult.requested} rows processed.`}
									</Text>
									{uploadResult.skipped > 0 && (
										<Text size='sm'>
											{uploadResult.skipped} staged row
											{uploadResult.skipped === 1 ? '' : 's'} skipped because of
											row errors.
										</Text>
									)}
									{uploadResult.missingReferenceCount > 0 && (
										<Text size='sm'>
											{uploadResult.missingReferenceCount} missing reference
											{uploadResult.missingReferenceCount === 1 ? '' : 's'} were
											left unlinked.
										</Text>
									)}
								</Alert>
							)}

							{uploadResult?.type === 'error' && (
								<Alert color='red' icon={<X size={16} />} title='Upload failed'>
									<Text size='sm'>{uploadResult.message}</Text>
								</Alert>
							)}

							<Divider />
							<Group position='apart' align='center'>
								<Text size='sm' color='dimmed'>
									If you proceed now, {uploadPreview.successCount} row
									{uploadPreview.successCount === 1 ? '' : 's'} will upload and{' '}
									{uploadPreview.skippedRowCount} row
									{uploadPreview.skippedRowCount === 1 ? '' : 's'} will be
									skipped.
								</Text>
								<Group>
									<Button
										variant='light'
										color='gray'
										onClick={closeUploadModal}
										disabled={uploadProgress !== null}
									>
										{uploadResult?.type === 'success' ? 'Done' : 'Cancel'}
									</Button>
									<Button
										leftIcon={<Upload size={16} />}
										onClick={handleConfirmUpload}
										disabled={
											!uploadPreview.canUpload ||
											uploadProgress !== null ||
											uploadResult?.type === 'success'
										}
									>
										Upload {uploadPreview.successCount} valid row
										{uploadPreview.successCount === 1 ? '' : 's'}
									</Button>
								</Group>
							</Group>
						</>
					)}
				</Stack>
			</Modal>

			<Modal
				opened={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				title='Confirm Deletion'
				centered
			>
				{checkInToDeleteBlockReason ? (
					<Text mb='md' color='red'>
						{checkInToDeleteBlockReason}
					</Text>
				) : (
					<Text mb='md'>
						Are you sure you want to permanently delete{' '}
						<strong>{checkInToDelete?.travelerName}</strong>'s check-in?
					</Text>
				)}
				<Group position='right'>
					<Button variant='light' onClick={() => setDeleteModalOpen(false)}>
						Cancel
					</Button>
					<Button
						color='red'
						onClick={handleDeleteConfirmed}
						disabled={Boolean(checkInToDeleteBlockReason)}
					>
						Permanently Delete
					</Button>
				</Group>
			</Modal>
		</Box>
	)
}

export default ManageCheckInsPage
